/**
 * Handlers for opening document-upload screenshot viewer modals.
 * Provides both a legacy Firestore-based viewer and a newer Postgres-backed
 * modal design with AbortController support.
 * @module handleDocUploadScreenshot
 */

import { FirestoreService } from "../api/firebase/firestoreService";
import { singleOrderDetailsGenericCallForPostgres } from "../api/postgreSQL/postCalls/singleOrderDetailsGenericCallForPostgres";
import { AuthStatusOptionModel } from "../data-model/authStatusOptions";
import { AuthOnFile, MedicalPaOrder } from "../data-model/medicalPaOrdersModel";
import { ModalId } from "../enums/modalId";
import { SingleOrderDetailsTableName } from "../enums/singleOrderDetailsTableName";
import { getFilePathsForModal } from "../pages/worklists/external/allPaOrdersForExternal/utils/allPaOrdersForExternalHelper";
import { setMetaData, setOpenedModalId } from "../redux/slice/modalSlice";
import { AppDispatch } from "../redux/store/store";
import { logError } from "./customLogger";
import { generateImageDataForOrder } from "./documentScreenshotUtils";
import { getOrgIdForFetchExternalWorklist } from "./organizationHelper";

/**
 * Opens the legacy image viewer modal with file paths for a given order.
 * Fetches the full order document from Firestore and extracts file paths.
 *
 * @param id - Order ID to fetch document data for
 * @param dispatch - Redux dispatch function
 * @param authStatusOptions - Auth status options (unused but retained for signature compatibility)
 */
export const handleDocUploadScreenshotViewForOrders = async (
  id: string,
  dispatch: AppDispatch,
  authStatusOptions: AuthStatusOptionModel[],
) => {
  dispatch(
    setOpenedModalId({
      id: ModalId.IMAGE_VIEWER_MODAL,
      metaData: { showLoader: true },
    }),
  );

  try {
    const document = (await FirestoreService.getDocument(
      `/medical_pa_orders/${id}`,
    )) as MedicalPaOrder;
    const filePaths = getFilePathsForModal(document);
    dispatch(
      setMetaData({
        filePaths: filePaths,
        showLoader: false,
      }),
    );
  } catch (error) {
    logError(error as Error);
    dispatch(
      setMetaData({
        filePaths: [],
        showLoader: false,
      }),
    );
  }
};

/**
 * Opens the document screenshot viewer modal (new design) for a given order.
 * Fetches auth-on-file data from Postgres, generates badge images, and populates
 * the modal via Redux metadata. Aborts any previous in-flight request before
 * starting a new one to prevent stale data.
 *
 * @param id - Order ID to fetch auth-on-file data for
 * @param dispatch - Redux dispatch function
 * @param abortControllerRef - Mutable ref holding the active AbortController; updated on each call
 */
export const handleDocUploadScreenshotViewForOrdersForNewModalDesign = async (
  id: string,
  dispatch: AppDispatch,
  abortControllerRef?: React.MutableRefObject<AbortController | null>,
) => {
  abortControllerRef?.current?.abort();
  const controller = new AbortController();
  if (abortControllerRef) {
    abortControllerRef.current = controller;
  }
  dispatch(
    setOpenedModalId({
      id: ModalId.DOCUMENT_SCREENSHOT_VIEWER_MODAL,
      metaData: { showLoading: true },
    }),
  );

  try {
    const authOnFile = (await singleOrderDetailsGenericCallForPostgres(
      {
        order_id: id,
        org_id: getOrgIdForFetchExternalWorklist(),
        table_name: SingleOrderDetailsTableName.AUTH_ON_FILE,
      },
      controller.signal,
    )) as {
      auth_on_file: AuthOnFile;
    };
    const imagesWithBadges = generateImageDataForOrder(
      authOnFile?.auth_on_file ?? undefined,
    );
    dispatch(
      setMetaData({
        imagesWithBadges: imagesWithBadges,
        showLoading: false,
      }),
    );
  } catch (error) {
    logError(error as Error);
    dispatch(
      setMetaData({
        imagesWithBadges: [],
        showLoading: false,
      }),
    );
  }
};
